import { getFunderMatchingBudgetRatio } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import {
  DEFAULT_MAX_REMAINING_BUDGET,
  singleLayerSProcess,
} from "algorithm/singleLayerSProcess";
import type { FunderLayerInput } from "./recommenderPreferences";

/** Share standard and matching endorsements independently. */
export function runFunderLayer(round: RoundAggregate, input: FunderLayerInput) {
  const ratios = input.funders.map((funder) =>
    getFunderMatchingBudgetRatio(round, funder),
  );

  const runPhase = (matching: boolean) => {
    const shares = ratios.map((ratio) => (matching ? ratio : 1 - ratio));
    const toleranceShare = Math.min(
      1,
      ...shares.filter(
        (share, f) => share > 0 && input.funderBudgets.get(f) > 0,
      ),
    );
    const preferences = input.funderPreferenceLists.map((preference, f) =>
      preference
        .mulColumns(shares[f]!)
        .rename("tranche -> tranche", "recommender+hold -> evaluable+hold"),
    );
    const output = singleLayerSProcess(
      input.funderBudgets
        .map((budget, f) => budget * shares[f]!)
        .rename("funder -> evaluator"),
      preferences,
      {
        debugLabel: `funder-layer:${matching ? "matching" : "standard"}`,
        maxRemainingBudget:
          Math.min(DEFAULT_MAX_REMAINING_BUDGET, 1) * toleranceShare,
        maxEpochs: 2000,
      },
    );
    return {
      accounts: output.accounts,
      guarantees: output.guarantees,
      remainingBudget: output.remainingBudget,
    };
  };

  const standard = runPhase(false);
  const matching = runPhase(true);
  return {
    accounts: standard.accounts.add(matching.accounts),
    guarantees: standard.guarantees.add(matching.guarantees),
    standardAccounts: standard.accounts,
    matchingAccounts: matching.accounts,
    matchingGuarantees: matching.guarantees,
    remainingBudget: standard.remainingBudget + (matching.remainingBudget ?? 0),
  };
}
